import { type ApiError } from "./types";
export type Http = typeof http;
export type ApiRequestMethods = "GET" | "PUT" | "POST" | "DELETE";
export type ServiceEndpointMeta = [method: ApiRequestMethods, url: string];
export type Meta<TKeys> = Record<keyof TKeys, ServiceEndpointMeta>;

type FetchData<T> = {
	params?: { [key: string]: any }; // eslint-disable-line
	body?: T;
	queryStrings?: { [key: string]: any }; // eslint-disable-line
	/** Custom headers (e.g. Cookie) – use when forwarding from server-side requests */
	headers?: Record<string, string>;
};

export type ApiData<TSuccess> = {
	statusCode: number;
	data?: TSuccess;
	error?: ApiError | unknown;
};

const getLanguage = (): string => {
	if (typeof window === "undefined") {
		return "en";
	}
	return document.documentElement.lang || "en";
};

export const http = {
	fetch: <TResponse, TRequest = unknown>(
		meta: ServiceEndpointMeta,
		data?: FetchData<TRequest>,
	): Promise<ApiData<TResponse>> => {
		const [method, endpoint] = meta;
		const url = endpoint;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const fetchSettings: globalThis.RequestInit = {
			method,
			credentials: "include",
			headers: {
				"content-type": "application/json;charset=UTF-8",
				...data?.headers,
			},
		};

		if (data?.body) {
			fetchSettings.body = JSON.stringify(data.body);
		}

		let finalUrl = url;

		if (data?.params) {
			Object.keys(data.params).forEach((param) => {
				finalUrl = url.replace(`/:${param}`, `/${data.params?.[param]}`);
			});
		}

		finalUrl = `${finalUrl}?sc_lang=${getLanguage()}`;

		if (typeof window === "undefined") {
			// full internal url with site. Used server side, like getStaticProps
			finalUrl = `${process.env.SITECORE_API_HOST}${finalUrl}&sc_site=${process.env.SITECORE_SITE_NAME}`;
		}

		if (data?.queryStrings) {
			const queryStrings: string[] = [];
			Object.keys(data.queryStrings).forEach((queryStringKey) => {
				const queryStringValue = data.queryStrings?.[queryStringKey]?.toString();
				if (queryStringValue) {
					queryStrings.push(`${queryStringKey}=${queryStringValue}`);
				}
			});

			finalUrl = `${finalUrl}&${queryStrings.join("&")}`;
		}

		const promise = new Promise<ApiData<TResponse>>((resolve, reject) => {
			fetch(finalUrl, fetchSettings)
				.then((response) => {
					const { status, ok } = response;
					if (status === 204) {
						resolve({ statusCode: status, data: undefined });
					}

					response
						.json()
						// eslint-disable-next-line
						.then((jsonResponse: any) => {
							if (!ok) {
								if (status === 500) {
									// InternalServerError
									console.error(jsonResponse);
								}

								reject({
									statusCode: status,
									error: jsonResponse,
								});

								return;
							}

							resolve({
								statusCode: status,
								data: jsonResponse,
							});
						})
						.catch((error) => {
							reject(error);
						});
				})
				.catch((error) => {
					reject(error);
				});
		});

		return promise;
	},
};
